import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TicketManualTimeEntry.css';

/**
 * TicketManualTimeEntry Component - Minimal Design
 * Allows users to add, edit, and view manual time entries for a ticket
 */

// API Base URL - Change this based on environment
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://helpdeskapi.fidaglobal.com/api';

const TicketManualTimeEntry = ({ ticketMasterId, authToken, userId }) => {
  const [timeEntries, setTimeEntries] = useState([]);
  const [totalTime, setTotalTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Stopwatch/Timer state
  const [isStopwatchActive, setIsStopwatchActive] = useState(false);
  const [stopwatchTime, setStopwatchTime] = useState(0); // in seconds
  const [stopwatchStartTime, setStopwatchStartTime] = useState(null);
  const [showStopwatchForm, setShowStopwatchForm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    startTime: '',
    endTime: '',
    description: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch time entries
  const fetchTimeEntries = async () => {
    setLoading(true);
    try {
      const [entriesRes, totalRes] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/manual/ticket/${ticketMasterId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        ),
        axios.get(
          `${API_BASE_URL}/manual/total/${ticketMasterId}`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        )
      ]);

      if (entriesRes.data.success) {
        setTimeEntries(entriesRes.data.data || []);
      }

      if (totalRes.data.success) {
        setTotalTime(totalRes.data.data);
      }
    } catch (err) {
      console.error('Error fetching time entries:', err);
      setError('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (ticketMasterId && authToken) {
      fetchTimeEntries();
    }
  }, [ticketMasterId, authToken]);

  // Stopwatch timer effect
  useEffect(() => {
    let interval;
    if (isStopwatchActive) {
      interval = setInterval(() => {
        setStopwatchTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStopwatchActive]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      startTime: '',
      endTime: '',
      description: ''
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  // Add or update time entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.startTime || !formData.endTime) {
      setError('Please enter both start and end time');
      return;
    }

    const startTime = new Date(formData.startTime);
    const endTime = new Date(formData.endTime);

    if (endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        // Update existing entry
        const response = await axios.put(
          `${API_BASE_URL}/manual/${editingId}`,
          {
            startTime: formData.startTime,
            endTime: formData.endTime,
            description: formData.description
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.success) {
          setSuccess('Time entry updated successfully');
          resetForm();
          await fetchTimeEntries();
        }
      } else {
        // Add new entry
        const response = await axios.post(
          `${API_BASE_URL}/manual/add`,
          {
            ticketMasterId,
            userId,
            startTime: formData.startTime,
            endTime: formData.endTime,
            description: formData.description
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.success) {
          setSuccess('Time entry added successfully');
          resetForm();
          await fetchTimeEntries();
        }
      }
    } catch (err) {
      console.error('Error saving time entry:', err);
      setError(err.response?.data?.message || 'Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  // Edit time entry
  const handleEdit = (entry) => {
    setEditingId(entry.ManualTimeEntryId);
    setFormData({
      startTime: new Date(entry.StartTime).toISOString().slice(0, 16),
      endTime: new Date(entry.EndTime).toISOString().slice(0, 16),
      description: entry.Description || ''
    });
    setShowForm(true);
  };

  // Delete time entry
  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/manual/${entryId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        setSuccess('Time entry deleted successfully');
        await fetchTimeEntries();
      }
    } catch (err) {
      console.error('Error deleting time entry:', err);
      setError('Failed to delete time entry');
    } finally {
      setLoading(false);
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format stopwatch time
  const formatStopwatchTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Start stopwatch
  const handleStartStopwatch = () => {
    setStopwatchStartTime(new Date());
    setIsStopwatchActive(true);
  };

  // Stop stopwatch
  const handleStopStopwatch = () => {
    setIsStopwatchActive(false);
  };

  // Reset stopwatch
  const handleResetStopwatch = () => {
    setIsStopwatchActive(false);
    setStopwatchTime(0);
    setStopwatchStartTime(null);
    setShowStopwatchForm(false);
  };

  // Submit stopwatch time (save to database)
  const handleSubmitStopwatch = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (stopwatchTime === 0) {
      setError('Please start the timer and track some time');
      return;
    }

    const startTime = new Date(stopwatchStartTime);
    const endTime = new Date(startTime.getTime() + stopwatchTime * 1000);

    const description = document.getElementById('stopwatchDescription')?.value || '';

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/manual/add`,
        {
          ticketMasterId,
          userId,
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          description: description
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.data.success) {
        setSuccess('Time entry saved successfully');
        handleResetStopwatch();
        await fetchTimeEntries();
      }
    } catch (err) {
      console.error('Error saving stopwatch time:', err);
      setError(err.response?.data?.message || 'Failed to save time entry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manual-time-entry-container">
      <div className="time-entry-header">
        <h3>Manual Time Entry</h3>
        <div className="header-buttons">
          <button
            className={`btn-add-time ${showForm ? 'hide' : ''}`}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            disabled={loading}
          >
            Add Time Entry
          </button>
          <button
            className={`btn-stopwatch ${showStopwatchForm ? 'active' : ''}`}
            onClick={() => setShowStopwatchForm(!showStopwatchForm)}
            disabled={loading}
          >
            Stopwatch
          </button>
        </div>
      </div>

      {/* Stopwatch Timer */}
      {showStopwatchForm && (
        <div className="stopwatch-container">
          <div className="stopwatch-display">
            <div className={`stopwatch-time ${isStopwatchActive ? 'running' : ''}`}>
              {formatStopwatchTime(stopwatchTime)}
            </div>
          </div>

          <div className="stopwatch-controls">
            {!isStopwatchActive ? (
              <button
                className="btn-start"
                onClick={handleStartStopwatch}
                disabled={loading}
              >
                Start
              </button>
            ) : (
              <button
                className="btn-stop"
                onClick={handleStopStopwatch}
                disabled={loading}
              >
                Stop
              </button>
            )}

            <button
              className="btn-reset"
              onClick={handleResetStopwatch}
              disabled={loading || isStopwatchActive}
            >
              Reset
            </button>
          </div>

          {stopwatchTime > 0 && !isStopwatchActive && (
            <form className="stopwatch-form" onSubmit={handleSubmitStopwatch}>
              <div className="form-group">
                <label htmlFor="stopwatchDescription">Description</label>
                <textarea
                  id="stopwatchDescription"
                  placeholder="What did you work on?"
                  disabled={loading}
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Submit Time'}
                </button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleResetStopwatch}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Total Time Summary - Minimal */}
      {totalTime && (
        <div className="time-summary">
          <div className="summary-item">
            <span className="summary-label">Total</span>
            <span className="summary-value total-time">
              {formatDuration(totalTime.TotalDurationMinutes)}
            </span>
          </div>
        </div>
      )}

      {/* Messages */}
      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Form */}
      {showForm && (
        <form className="time-entry-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="startTime">Start Time</label>
            <input
              type="datetime-local"
              id="startTime"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="endTime">End Time</label>
            <input
              type="datetime-local"
              id="endTime"
              name="endTime"
              value={formData.endTime}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="What did you work on?"
              disabled={loading}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? 'Saving...' : editingId ? 'Update Entry' : 'Add Entry'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={resetForm}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Time Entries List */}
      <div className="time-entries-list">
        {loading && !showForm && <div className="loading">Loading...</div>}

        {timeEntries.length === 0 && !showForm && (
          <div className="no-entries">No time entries yet</div>
        )}

        {timeEntries.length > 0 && (
          <div className="time-entries-table-wrapper">
            <table className="time-entries-table">
              <thead>
                <tr>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Duration</th>
                  <th>Description</th>
                  <th>User</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {timeEntries.map((entry) => (
                  <tr key={entry.ManualTimeEntryId}>
                    <td>{formatDateTime(entry.StartTime)}</td>
                    <td>{formatDateTime(entry.EndTime)}</td>
                    <td>
                      <span className="duration-badge">
                        {formatDuration(entry.DurationMinutes)}
                      </span>
                      {entry.IsEdited && <span className="edited-badge">Edited</span>}
                    </td>
                    <td>{entry.Description || '—'}</td>
                    <td>{entry.UserName}</td>
                    <td>{new Date(entry.CreatedDate).toLocaleDateString()}</td>
                    <td>
                      <div className="time-entry-actions-cell">
                        <button
                          className="btn-edit"
                          onClick={() => handleEdit(entry)}
                          disabled={loading}
                          title="Edit entry"
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(entry.ManualTimeEntryId)}
                          disabled={loading}
                          title="Delete entry"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketManualTimeEntry;